import api from './api';

// Types for admin dashboard
export interface AdminStats {
  totalUsers: number;
  totalTrainingCenters: number;
  totalTrainings: number;
  totalRevenue: number;
  activeSubscriptions: number;
  monthlyGrowth: number;
  completionRate: number;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  userType: string;
  isActive: boolean;
  createdAt: string;
  lastLogin: string;
  phone?: string;
  city?: string;
  wilaya?: string;
}

export interface TrainingCenter {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city?: string;
  country_id: number;
  country: {
    id: number;
    name: string;
  };
  wilaya_id: number;
  wilaya: {
    id: number;
    name: string;
  };
  isActive: boolean;
  isVerified: boolean;
  subscriptionStatus: string;
  totalTrainings: number;
  createdAt: string;
  profile_type?: 'consultant' | 'training_center';
}

export interface Training {
  id: string;
  title: string;
  description?: string;
  full_description?: string;
  learning_objectives?: string;
  skills_gained?: string;
  target_audience?: string;
  trainer?: string;
  language?: string;
  format?: string;
  schedule_date?: string;
  trainingCenter: {
    id: string;
    name: string;
  };
  domaine: {
    id: string;
    name?: string;
    name_french?: string;
    name_arabic?: string;
    name_english?: string;
  };
  level: string;
  duration: string;
  pricing_dzd: number;
  isActive: boolean;
  isApproved: boolean;
  is_featured?: boolean;
  status?: 'pending' | 'approved' | 'refused';
  rejection_reason?: string;
  rejectionReason?: string;
  views: number;
  subscriptionsCount?: number;
  createdAt: string;
  startDate?: string;
  endDate?: string;
}



export interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  paymentMethod: string;
  description: string;
  userId: string;
  trainingCenterId?: string;
  subscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  plan: {
    id: string;
    name: string;
    price: number;
    duration: number;
  };
  trainingCenter: {
    id: string;
    name: string;
  };
  status: 'active' | 'expired' | 'cancelled';
  startDate: string;
  endDate: string;
  createdAt: string;
}

export interface PendingSubscription {
  id: string;
  center: {
    id: string;
    name: string;
    user: {
      email: string;
    };
  };
  subscription_plan: {
    id: string;
    title: string;
    price: number;
  };
  payment_receipt_filename: string;
  status: 'pending' | 'active' | 'expired' | 'rejected';
  admin_notes?: string;
  created_at: string;
}

export interface ChartData {
  group: string;
  key: string;
  value: number;
}

export interface AdminStatistics {
  totalUsers: number;
  totalTrainees: number;
  totalTrainingCenters: number;
  totalConsultants: number;
  totalServicesPublished: number;
  totalFormationsPublished: number;
  totalServiceViews: number;
  totalTrainingViews: number;
  totalTrainingSubscriptions: number;
  totalServiceSubscriptions: number;
  activeSubscriptions: number;
  totalReservations: number;
  totalTraineeSubscriptions: number;
  charts: {
    users: ChartData[];
    trainings: ChartData[];
    views: ChartData[];
  };
}

class AdminService {
  // Dashboard Stats
  async getDashboardStats(): Promise<AdminStats> {
    const response = await api.get('/admin/dashboard/stats');
    return response.data;
  }

  async getRevenueChart(): Promise<ChartData[]> {
    const response = await api.get('/admin/dashboard/revenue-chart');
    return response.data;
  }

  async getUsersChart(): Promise<ChartData[]> {
    const response = await api.get('/admin/dashboard/users-chart');
    return response.data;
  }

  // Users Management
  async getUsers(page: number = 1, limit: number = 10, search?: string): Promise<{ users: User[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });
    const response = await api.get(`/admin/users?${params}`);
    return response.data;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<void> {
    try {
      await api.patch(`/admin/users/${userId}/status`, { isActive });
    } catch (error) {
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      await api.delete(`/admin/users/${userId}`);
    } catch (error) {
      throw error;
    }
  }

  // Training Centers Management
  async getTrainingCenters(page: number = 1, limit: number = 10, search?: string): Promise<{ centers: TrainingCenter[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
    });
    const response = await api.get(`/admin/training-centers?${params}`);
    return response.data;
  }

  async getTrainingCenterById(id: string): Promise<any> {
    const response = await api.get(`/admin/training-centers/${id}`);
    return response.data;
  }

  async updateTrainingCenterStatus(centerId: string, isActive: boolean, isVerified: boolean): Promise<void> {
    try {
      await api.patch(`/admin/training-centers/${centerId}/status`, { isActive, isVerified });
    } catch (error) {
      throw error;
    }
  }

  // Trainings Management
  async getTrainings(page: number = 1, limit: number = 10, search?: string, status?: string): Promise<{ trainings: Training[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(status && { status }),
    });
    const response = await api.get(`/trainings/admin/trainings?${params}`);
    return response.data;
  }

  async getTrainingById(id: string): Promise<Training> {
    const response = await api.get(`/trainings/admin/trainings/${id}`);
    return response.data;
  }

  async approveTraining(trainingId: string): Promise<void> {
    try {
      await api.patch(`/trainings/admin/trainings/${trainingId}/approve`);
    } catch (error) {
      throw error;
    }
  }

  async rejectTraining(trainingId: string, reason: string): Promise<void> {
    try {
      await api.patch(`/trainings/admin/trainings/${trainingId}/reject`, { reason });
    } catch (error) {
      throw error;
    }
  }

  async featureTraining(trainingId: string, isFeatured: boolean): Promise<void> {
    try {
      const response = await api.patch(`/trainings/admin/trainings/${trainingId}/feature`, { is_featured: isFeatured });
    } catch (error) {
      throw error;
    }
  }

  // Send notification to training center about approval/rejection
  async sendTrainingNotification(
    trainingCenterId: string, 
    trainingId: string,
    status: 'approved' | 'rejected', 
    trainingTitle: string,
    reason?: string
  ): Promise<void> {
    try {
      await api.post('/admin/notifications/send-training-notification', {
        trainingCenterId,
        trainingId,
        status,
        trainingTitle,
        reason
      });
    } catch (error) {
      throw error;
    }
  }

async deleteTrainingCenter(centerId: string): Promise<void> {
  await api.delete(`/auth/training-center/${centerId}`);
}

  // Consultants Management
  async getConsultants(page: number = 1, limit: number = 10, search?: string): Promise<{ consultants: any[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      profile_type: 'consultant', // Filter for consultants only
      ...(search && { search })
    });
    // Use admin endpoint which returns all fields including isActive
    const response = await api.get(`/admin/training-centers?${params}`);
    return {
      consultants: response.data.centers || [],
      total: response.data.total || 0
    };
  }

  async getConsultantById(id: number): Promise<any> {
    // Use admin endpoint which returns all fields
    const response = await api.get(`/admin/training-centers/${id}`);
    return response.data;
  }

  async deleteConsultant(userId: string): Promise<void> {
    await api.delete(`/consultants/account/${userId}`);
  }

  async updateConsultantStatus(consultantId: string, isActive: boolean, isVerified?: boolean): Promise<void> {
    try {
      // Consultants are stored in the same table as training centers, so use the same endpoint
      // If isVerified is not provided, we'll pass true to keep it unchanged (backend should handle this)
      await api.patch(`/admin/training-centers/${consultantId}/status`, { 
        isActive, 
        isVerified: isVerified !== undefined ? isVerified : true
      });
    } catch (error) {
      throw error;
    }
  }




  // Subscriptions Management
  async getSubscriptions(page: number = 1, limit: number = 10, status?: string): Promise<{ subscriptions: Subscription[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(status && { status }),
    });
    const response = await api.get(`/admin/subscriptions?${params}`);
    return response.data;
  }

  // Payment Receipts Management
  async getPendingSubscriptions(page: number = 1, limit: number = 10): Promise<{ subscriptions: PendingSubscription[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await api.get(`/payment-receipts/admin/pending?${params}`);
    return response.data;
  }

  async approveSubscription(subscriptionId: string, adminNotes?: string): Promise<void> {
    try {
      // Validate subscriptionId is a number
      const id = parseInt(subscriptionId);
      if (isNaN(id)) {
        throw new Error(`Invalid subscription ID: ${subscriptionId}`);
      }

      const response = await api.patch(`/payment-receipts/admin/${id}/approve`, { adminNotes });
    } catch (error) {
      throw error;
    }
  }

  async rejectSubscription(subscriptionId: string, adminNotes: string): Promise<void> {
    try {
      // Validate subscriptionId is a number
      const id = parseInt(subscriptionId);
      if (isNaN(id)) {
        throw new Error(`Invalid subscription ID: ${subscriptionId}`);
      }

      const response = await api.patch(`/payment-receipts/admin/${id}/reject`, { adminNotes });
    } catch (error) {
      throw error;
    }
  }

  // Invoices Management
  async getInvoices(page: number = 1, limit: number = 10, status?: string): Promise<{ invoices: any[]; total: number }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status && status !== 'all') {
      params.append('status', status);
    }
    const response = await api.get(`/payment-receipts/admin/invoices?${params}`);
    return response.data;
  }

  async downloadInvoice(filename: string): Promise<Blob> {
    const response = await api.get(`/payment-receipts/admin/invoices/download/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Prestations Management
  async getConsultantServices(): Promise<any[]> {
    const response = await api.get('/trainings/admin/consultant-services');
    return response.data;
  }

  async getConsultantServiceDetails(id: number): Promise<any> {
    const response = await api.get(`/trainings/admin/consultant-services/${id}`);
    return response.data;
  }

  async approveConsultantService(id: number): Promise<void> {
    await api.patch(`/trainings/admin/consultant-services/${id}/approve`);
  }

  async rejectConsultantService(id: number, reason: string): Promise<void> {
    await api.patch(`/trainings/admin/consultant-services/${id}/reject`, { reason });
  }

  async deleteConsultantService(id: number): Promise<void> {
    await api.delete(`/trainings/admin/consultant-services/${id}`);
  }

  // Get comprehensive statistics with date filters
  async getStatistics(dateRange: 'daily' | 'month' | 'quarter' | 'year' | 'all' = 'all'): Promise<AdminStatistics> {
    const params = new URLSearchParams({
      dateRange,
    });
    const response = await api.get(`/admin/statistics?${params}`);
    return response.data;
  }

  // Get recent activity
  async getRecentActivity(limit: number = 10, offset: number = 0): Promise<{
    activities: Array<{
      id: string;
      type: string;
      action: string;
      user: string;
      date: string;
      status: string;
    }>;
    total: number;
    limit: number;
    offset: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await api.get(`/admin/recent-activity?${params}`);
    return response.data;
  }

}

export default new AdminService();
